import { useMutation, useQuery } from "@tanstack/react-query"
import { RefreshCw, X } from "lucide-react"
import type { ReactNode } from "react"
import { useId } from "react"

import { api } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { SelectorBuscable } from "@/componentes/ui/SelectorBuscable"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"
import type { BorradorRetencion } from "./tipos"

type Precarga = { nombre: string; rif: string | null }
type TasaRegistrada = { fecha: string; tasa: string }
type ClienteBreve = { id: number; razon_social: string; rif: string }
type ProveedorBreve = { id: number; nombre: string; rif: string | null }

/**
 * La cabecera del comprobante: quién retiene, a quién, sobre qué documento y con qué
 * porcentajes. Las líneas van aparte (`LineasRetencion`).
 *
 * ⚠ **Elegir de la lista PRECARGA nombre y RIF, y los dos se pueden corregir.** Un
 * tercero del puerto muchas veces no está en el catálogo, y lo que se imprime es lo
 * que queda escrito, no lo que diga la ficha.
 *
 * ⚠ **Los selectores sólo salen con su permiso de catálogo** (`cliente:ver`,
 * `proveedor:ver`). Sin él, la lista vendría con un 403; se escribe a mano.
 */
export function EditorRetencion({
  datos,
  onCambio,
}: {
  datos: BorradorRetencion
  onCambio: (datos: BorradorRetencion) => void
}) {
  const usuario = usarSesion((e) => e.usuario)
  const verClientes = tienePermiso(usuario, "cliente:ver")
  const verProveedores = tienePermiso(usuario, "proveedor:ver")
  const id = useId()
  const poner = <K extends keyof BorradorRetencion>(campo: K, valor: BorradorRetencion[K]) =>
    onCambio({ ...datos, [campo]: valor })

  const clientes = useQuery({
    queryKey: ["clientes", "activos"],
    queryFn: () => api.get<ClienteBreve[]>("/api/v1/clientes?limit=100&activo=true"),
    enabled: verClientes,
  })
  const proveedores = useQuery({
    queryKey: ["proveedores", "activos", "todos"],
    queryFn: () => api.get<ProveedorBreve[]>("/api/v1/proveedores?limit=100&activo=true"),
    enabled: verProveedores,
  })

  const precargar = useMutation({
    mutationFn: (q: { lado: "agente" | "beneficiario"; busca: string }) =>
      api.get<Precarga>(`/api/v1/retenciones-provisionales/precarga?${q.busca}`),
    onSuccess: (p, q) =>
      onCambio(
        q.lado === "agente"
          ? { ...datos, agente_nombre: p.nombre, agente_rif: p.rif ?? "" }
          : { ...datos, beneficiario_nombre: p.nombre, beneficiario_rif: p.rif ?? "" },
      ),
  })

  // La tasa del registro es una PROPUESTA: se escribe en el campo y se puede cambiar.
  const traerTasa = useMutation({
    mutationFn: (fecha: string) =>
      api.get<TasaRegistrada>(`/api/v1/retenciones-provisionales/tasa-registrada?fecha=${fecha}`),
    onSuccess: (t) =>
      onCambio({ ...datos, tasa_reajuste: sinCeros(t.tasa), fecha_reajuste: t.fecha }),
  })

  return (
    <div className="grid gap-4">
      <Grupo titulo="Agente de retención (cliente que paga)">
        {verClientes && (
          <div className="grid gap-1">
            <label htmlFor={`${id}-cliente`} className="text-xs font-medium">
              Precargar de un cliente del catálogo
            </label>
            <SelectorBuscable
              id={`${id}-cliente`}
              vacio="Ninguno: escribir a mano"
              placeholder="Buscar cliente…"
              opciones={(clientes.data ?? []).map((c) => ({
                valor: c.id,
                etiqueta: c.razon_social,
                detalle: c.rif,
              }))}
              valor={datos.cliente_id}
              onCambiar={(v) => {
                onCambio({ ...datos, cliente_id: v })
                if (v !== null) precargar.mutate({ lado: "agente", busca: `cliente_id=${v}` })
              }}
            />
          </div>
        )}
        <Campo rotulo="Nombre o razón social" requerido={true}>
          <input
            className="campo h-9 w-full text-sm"
            value={datos.agente_nombre}
            maxLength={200}
            onChange={(e) => poner("agente_nombre", e.target.value)}
          />
        </Campo>
        <Campo rotulo="RIF">
          <input
            className="campo h-9 w-full text-sm"
            value={datos.agente_rif}
            maxLength={30}
            onChange={(e) => poner("agente_rif", e.target.value)}
          />
        </Campo>
      </Grupo>

      <Grupo titulo="Beneficiario (a quien se le retiene)">
        {verProveedores && (
          <div className="grid gap-1">
            <label htmlFor={`${id}-proveedor`} className="text-xs font-medium">
              Precargar de un proveedor del catálogo
            </label>
            <SelectorBuscable
              id={`${id}-proveedor`}
              vacio="Ninguno: escribir a mano"
              placeholder="Buscar proveedor…"
              opciones={(proveedores.data ?? []).map((p) => ({
                valor: p.id,
                etiqueta: p.nombre,
                detalle: p.rif ?? undefined,
              }))}
              valor={datos.proveedor_id}
              onCambiar={(v) => {
                onCambio({ ...datos, proveedor_id: v })
                if (v !== null)
                  precargar.mutate({ lado: "beneficiario", busca: `proveedor_id=${v}` })
              }}
            />
          </div>
        )}
        <Campo rotulo="Nombre o razón social" requerido={true}>
          <input
            className="campo h-9 w-full text-sm"
            value={datos.beneficiario_nombre}
            maxLength={200}
            onChange={(e) => poner("beneficiario_nombre", e.target.value)}
          />
        </Campo>
        <Campo rotulo="RIF">
          <input
            className="campo h-9 w-full text-sm"
            value={datos.beneficiario_rif}
            maxLength={30}
            onChange={(e) => poner("beneficiario_rif", e.target.value)}
          />
        </Campo>
      </Grupo>

      <Grupo titulo="Documento">
        <div className="grid gap-3 sm:grid-cols-2">
          <Campo rotulo="Fecha" requerido={true}>
            <input
              type="date"
              className="campo tabular-nums h-9 w-full text-sm"
              value={datos.fecha}
              onChange={(e) => poner("fecha", e.target.value)}
            />
          </Campo>
          <Campo rotulo="Preliquidación, pedido o factura">
            <input
              className="campo h-9 w-full text-sm"
              value={datos.documento}
              maxLength={60}
              onChange={(e) => poner("documento", e.target.value)}
            />
          </Campo>
          <Campo rotulo="B/L">
            <input
              className="campo h-9 w-full text-sm"
              value={datos.bl}
              maxLength={60}
              onChange={(e) => poner("bl", e.target.value)}
            />
          </Campo>
          <Campo rotulo="Tasa de emisión (Bs/USD)">
            <input
              className="campo tabular-nums h-9 w-full text-sm"
              inputMode="decimal"
              value={datos.tasa_cambio}
              onChange={(e) => poner("tasa_cambio", e.target.value)}
            />
          </Campo>
        </div>
        <Campo rotulo="Concepto">
          <input
            className="campo h-9 w-full text-sm"
            value={datos.concepto}
            maxLength={300}
            onChange={(e) => poner("concepto", e.target.value)}
          />
        </Campo>
      </Grupo>

      <Grupo titulo="Reajuste cambiario">
        <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
          Si se paga a una tasa distinta a la de emisión, escríbela aquí: el total de la factura,
          las retenciones y el neto se recalculan con ella y el comprobante sale reajustado. Vacía,
          rige la de emisión.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Campo rotulo="Fecha del pago">
            <input
              type="date"
              className="campo tabular-nums h-9 w-full text-sm"
              value={datos.fecha_reajuste}
              onChange={(e) => poner("fecha_reajuste", e.target.value)}
            />
          </Campo>
          <Campo rotulo="Tasa de reajuste (Bs/USD)">
            <input
              className="campo tabular-nums h-9 w-full text-sm"
              inputMode="decimal"
              value={datos.tasa_reajuste}
              onChange={(e) => poner("tasa_reajuste", e.target.value)}
            />
          </Campo>
        </div>
        <div className="flex flex-wrap gap-2">
          <Boton
            disabled={!datos.fecha_reajuste || traerTasa.isPending}
            title={datos.fecha_reajuste ? undefined : "Indica primero la fecha del pago"}
            onClick={() => traerTasa.mutate(datos.fecha_reajuste)}
          >
            <RefreshCw size={15} aria-hidden={true} />
            Traer la tasa registrada
          </Boton>
          {(datos.tasa_reajuste || datos.fecha_reajuste) && (
            <Boton onClick={() => onCambio({ ...datos, tasa_reajuste: "", fecha_reajuste: "" })}>
              <X size={15} aria-hidden={true} />
              Quitar el reajuste
            </Boton>
          )}
        </div>
        {traerTasa.error && (
          <p className="text-xs" role="alert" style={{ color: "var(--destructive)" }}>
            {traerTasa.error.message}
          </p>
        )}
        {datos.tasa_reajuste.trim() !== "" && datos.tasa_cambio.trim() === "" && (
          <p className="text-xs" role="alert" style={{ color: "var(--destructive)" }}>
            Para reajustar hace falta la tasa de emisión del documento.
          </p>
        )}
      </Grupo>

      <Grupo titulo="Porcentajes">
        <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
          Cámbialos aquí y el comprobante se recalcula. Qué servicio lleva cada retención se marca
          en sus líneas.
        </p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {(
            [
              ["alicuota_iva", "IVA %"],
              ["porcentaje_ret_iva", "Ret. IVA %"],
              ["porcentaje_ret_islr", "Ret. ISLR %"],
              ["sustraendo_islr", "Sustraendo Bs"],
            ] as const
          ).map(([campo, rotulo]) => (
            <Campo key={campo} rotulo={rotulo}>
              <input
                className="campo tabular-nums h-9 w-full text-sm"
                inputMode="decimal"
                value={datos[campo]}
                onChange={(e) => poner(campo, e.target.value)}
              />
            </Campo>
          ))}
        </div>
      </Grupo>

      <Grupo titulo="Pie">
        <Campo rotulo="Firma">
          <input
            className="campo h-9 w-full text-sm"
            value={datos.firmante}
            maxLength={200}
            onChange={(e) => poner("firmante", e.target.value)}
          />
        </Campo>
        <Campo rotulo="Observaciones">
          <textarea
            className="campo min-h-16 w-full py-2 text-sm"
            value={datos.observaciones}
            maxLength={500}
            onChange={(e) => poner("observaciones", e.target.value)}
          />
        </Campo>
      </Grupo>
    </div>
  )
}

function Grupo({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <fieldset className="superficie grid gap-3 p-4">
      <legend className="rotulo px-1">{titulo}</legend>
      {children}
    </fieldset>
  )
}

export function Campo({
  rotulo,
  requerido = false,
  children,
}: {
  rotulo: string
  requerido?: boolean
  children: ReactNode
}) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: el control llega como hijo
    <label className="grid gap-1">
      <span className="text-xs font-medium">
        {rotulo}
        {requerido && <span aria-hidden={true}> *</span>}
      </span>
      {children}
    </label>
  )
}

/** «476.570100» → «476.5701»: la tasa del registro sin los ceros de relleno. */
function sinCeros(tasa: string): string {
  return tasa.includes(".") ? tasa.replace(/0+$/, "").replace(/\.$/, "") : tasa
}
